import { THREE, errorManager, modes, gsap } from '@powerplay/common';
import { Howler } from 'howler';
import { Audio } from './Audio';
import { AudioType } from './types';
import { AudioRandomizer } from './AudioRandomizer';
class AudioManager {
    audioRepository = [];
    audioVolume = {};
    playingAudioIndex = {};
    playingAudioRepository = {};
    threeJsAudioListener;
    threeJsAudioLoader;
    TUTORIAL_EXCEPTION_CATEGORY = 'commentators';
    ignoredAudio = [];
    playedAudioIndexes = {};
    static PATH_ASSETS = '';
    spriteCache = {};
    usedSpriteAudioCache = {};
    audioRandomizer = new AudioRandomizer();
    NO_MORE_AUDIO_PLAYABLE = 'no-more-audio-playable';
    create() {
        this.threeJsAudioListener = new THREE.AudioListener();
        this.threeJsAudioLoader = new THREE.AudioLoader();
    }
    add = (audioObject) => {
        this.handleSpriteAudioAddition(audioObject);
        if (this.checkIgnoredSound(audioObject))
            return;
        const files = this.loadRandomSoundAmount(audioObject);
        this.saveToAudioRepository(files, audioObject);
    };
    checkIgnoredSound(audioObject) {
        if (audioObject.ignoreModes?.includes(modes.mode)) {
            this.ignoredAudio.push(...audioObject.files);
            return true;
        }
        return false;
    }
    handleSpriteAudioAddition(audioObject) {
        if (!audioObject.sprite)
            return;
        const nameArray = [];
        const spriteName = audioObject.files[0];
        Object.keys(audioObject.sprite).forEach((key) => nameArray.push(key));
        this.spriteCache[spriteName] = nameArray;
    }
    loadRandomSoundAmount(audioObject) {
        if (audioObject.sprite)
            return audioObject.files;
        return audioObject.loadRandomCount ?
            this.audioRandomizer.getRandomFiles(audioObject.loadRandomCount, [...audioObject.files]) :
            audioObject.files;
    }
    saveToAudioRepository(fileNames, audioObject) {
        fileNames.forEach((audioName) => {
            this.audioRepository.push(new Audio(audioName, audioObject, this.threeJsAudioLoader));
        });
    }
    isIgnoredAudio(audioName) {
        if (this.ignoredAudio.includes(audioName))
            return true;
        const spriteName = this.getAudioNameFromSprites(audioName);
        if (this.ignoredAudio.includes(spriteName))
            return true;
        return false;
    }
    play(audioName, fadeDuration = 0, loop, volume, playOnce = false) {
        if (this.isAudioPlaying(audioName) || this.isIgnoredAudio(audioName))
            return;
        const [audio, spriteName] = this.getRandomAudio(audioName, playOnce);
        if (spriteName === this.NO_MORE_AUDIO_PLAYABLE)
            return;
        let innerLoop = false;
        if (audio === undefined)
            return;
        if (loop === undefined)
            innerLoop = audio.audioObject.loop;
        if (!innerLoop) {
            audio?.howl?.on('end', () => {
                if (spriteName === '') {
                    this.stopAudioByName(audio.audioName);
                }
                else {
                    this.stopAudioByName(spriteName);
                }
            });
        }
        const randomDelay = this.audioRandomizer.getRandomDelay(audio);
        const volumeParam = volume || audio.audioObject.volume;
        let audioId;
        if (audio.audioObject.playDirect && audio?.buffer) {
            const audioObject = new THREE.Audio(this.threeJsAudioListener);
            audioObject.setBuffer(audio.buffer);
            audioObject.setVolume(0.2);
            audioObject.setLoop(innerLoop);
            audioObject.play();
        }
        else {
            audioId = this.playAndSetupAudioObjectProperties(audio, spriteName, innerLoop, volumeParam, fadeDuration);
            if (audioId === undefined)
                return;
            if (randomDelay > 0) {
                gsap.to({}, {
                    onComplete: this.fade,
                    onCompleteParams: [audioId, volumeParam, fadeDuration],
                    callbackScope: this,
                    duration: randomDelay / 1000
                });
                return;
            }
            if (fadeDuration > 0)
                this.fade(audioId, volumeParam, fadeDuration);
        }
    }
    getPlayingAudioIndex(audioName) {
        let audioIndex = this.playingAudioIndex[audioName];
        if (!audioIndex) {
            for (const [key, index] of Object.entries(this.playingAudioIndex)) {
                if (key.includes(audioName))
                    audioIndex = index;
            }
        }
        return audioIndex;
    }
    changeAudioVolume(audioName, newVolume) {
        const playingAudioIndex = this.getPlayingAudioIndex(audioName);
        const audioPlaying = this.playingAudioRepository[playingAudioIndex];
        if (audioPlaying === undefined)
            return;
        const lastVolume = this.audioVolume[audioName] ?? -1;
        const roundedVolume = Number(newVolume.toFixed(2));
        if (lastVolume !== roundedVolume) {
            audioPlaying.howl?.volume(roundedVolume);
            this.audioVolume[audioName] = roundedVolume;
        }
    }
    stopAudio(audioId) {
        this.fade(audioId, 0, 0);
    }
    isAudioGroupPlaying(audioGroup) {
        return !!Object.values(this.playingAudioRepository).find((audio) => audio.audioObject.group === audioGroup);
    }
    isAudioPlaying(audioName) {
        return !!this.getPlayingAudioIndex(audioName);
    }
    playAndSetupAudioObjectProperties(audio, audioName, loop, volume, fadeDuration) {
        if (!audio?.howl)
            return undefined;
        let audioId;
        if (audio.isSprite) {
            audioId = audio.howl.play(audioName);
        }
        else {
            audioId = audio.howl.play();
        }
        if (audioName === '') {
            this.playingAudioIndex[audio.audioName] = audioId;
        }
        else {
            this.playingAudioIndex[audioName] = audioId;
        }
        this.playingAudioRepository[audioId] = audio;
        audio.howl.loop(loop, audioId);
        if (fadeDuration > 0) {
            audio.howl.volume(0, audioId);
        }
        else {
            audio.howl.volume(Number((volume !== undefined) ? volume : audio.howl.volume), audioId);
        }
        this.audioRandomizer.setRandomness(audio, audioId);
        return audioId;
    }
    fadeOutAudio(audioName, duration) {
        this.fade(this.getPlayingAudioIndex(audioName), 0, duration);
    }
    fade(audioId, volumeTo, duration) {
        const audioPlaying = this.playingAudioRepository[audioId];
        if (audioPlaying === undefined)
            return;
        const volume = audioPlaying.howl?.volume();
        if (typeof volume === 'number') {
            audioPlaying.howl?.fade(volume, volumeTo, duration, audioId);
        }
        if (volumeTo === 0) {
            gsap.to({}, {
                onComplete: this.onFadeComplete,
                onCompleteParams: [audioPlaying, volumeTo, audioId],
                callbackScope: this,
                duration: duration / 1000
            });
        }
    }
    onFadeComplete = (audio, to, id) => {
        if (to === 0 && audio.howl) {
            audio.howl.volume(audio.audioObject.volume);
            audio.howl.stop(id);
            this.removeAudioFromPlaying(id);
        }
    };
    removeAudioFromPlaying(audioId) {
        const audio = this.playingAudioRepository[audioId];
        if (!audio)
            return;
        if (audio.howl?.playing(audioId))
            audio.howl.stop(audioId);
        const name = audio.spriteAudioName !== '' ? audio.spriteAudioName : audio.audioName;
        delete this.playingAudioIndex[name];
        delete this.playingAudioRepository[audioId];
    }
    stopOtherCommentatorAudio() {
        const commentatorsToMute = [1];
        commentatorsToMute.forEach((category) => {
            for (const [key, audio] of Object.entries(this.playingAudioRepository)) {
                if (audio.audioName.includes(String(category)) &&
                    audio.howl?.playing() &&
                    audio.audioName !== 'commentator-global-0') {
                    this.removeAudioFromPlaying(Number(key));
                }
            }
        });
    }
    getRandomAudio(audioName, playOnce) {
        const audioType = this.decideAudioType(audioName);
        if (audioType === AudioType.sprite) {
            return this.handleSpritePlayAudio(audioName, playOnce);
        }
        else {
            return this.handleNormalPlayAudio(audioName, playOnce);
        }
    }
    decideAudioType(audioName) {
        const audioFilter = this.audioRepository.filter((audioObject) => audioObject.audioName.startsWith(audioName));
        if (audioFilter.length > 0)
            return AudioType.normal;
        return AudioType.sprite;
    }
    handleSpritePlayAudio(audioName, playOnce) {
        const endAudioName = this.findAudioInSprites(audioName);
        const endSpriteAudio = this.getSpriteAudioObject(endAudioName);
        if (endSpriteAudio !== undefined) {
            let name = this.randomizeSprite(endSpriteAudio, audioName, playOnce);
            if (name === '') {
                name = audioName;
            }
            endSpriteAudio.spriteAudioName = name;
            return [endSpriteAudio, name];
        }
        else {
            throw new Error(errorManager.showBox(`zvuk ${audioName} neexistuje, niekde je chyba pri loadingu`));
        }
    }
    randomizeSprite(endSpriteAudio, audioName, playOnce) {
        if (!endSpriteAudio.randomizeSprite)
            return '';
        if (endSpriteAudio.subset) {
            const unusedAudioNamesSubset = this.getUnusedAudioNamesSubset(endSpriteAudio, audioName);
            if (unusedAudioNamesSubset.length === 0)
                return this.NO_MORE_AUDIO_PLAYABLE;
            const selectAudio = this.audioRandomizer.getRandomSpriteName(unusedAudioNamesSubset);
            this.cacheAudioSpriteSubset(selectAudio, endSpriteAudio, audioName, playOnce);
            return selectAudio;
        }
        else {
            const unusedAudioNames = this.getUnusedAudioNames(endSpriteAudio);
            if (unusedAudioNames.length === 0)
                return this.NO_MORE_AUDIO_PLAYABLE;
            const selectAudio = this.audioRandomizer.getRandomSpriteName(unusedAudioNames);
            this.cacheAudioSprites(selectAudio, endSpriteAudio, playOnce);
            return selectAudio;
        }
    }
    getUnusedAudioNamesSubset(audio, audioName) {
        if (!this.usedSpriteAudioCache[audioName]) {
            return audio.spriteNames.filter(name => name.includes(audioName));
        }
        return audio.spriteNames
            .filter(name => name.includes(audioName))
            .filter(name => !this.usedSpriteAudioCache[audioName].includes(name));
    }
    cacheAudioSpriteSubset(name, audio, audioName, playOnce) {
        const spritesLength = audio.spriteNames.filter(nameItem => nameItem.includes(audioName)).length;
        if (!this.usedSpriteAudioCache[audioName]) {
            this.usedSpriteAudioCache[audioName] = [name];
            this.checkIfAllAudiosPlayed(audioName, spritesLength, playOnce);
            return;
        }
        this.usedSpriteAudioCache[audioName] = [
            ...this.usedSpriteAudioCache[audioName],
            name
        ];
        this.checkIfAllAudiosPlayed(audioName, spritesLength, playOnce);
    }
    checkIfAllAudiosPlayed(audioName, spritesLength, playOnce) {
        if (this.usedSpriteAudioCache[audioName].length === spritesLength && !playOnce) {
            this.resetSpriteCache(audioName);
        }
    }
    resetSpriteCache(audioName) {
        this.usedSpriteAudioCache[audioName] = [];
    }
    cacheAudioSprites(name, audio, playOnce) {
        if (!this.usedSpriteAudioCache[audio.spriteAudioName]) {
            this.usedSpriteAudioCache[audio.spriteAudioName] = [name];
            return;
        }
        this.usedSpriteAudioCache[audio.spriteAudioName] = [
            ...this.usedSpriteAudioCache[audio.spriteAudioName],
            name
        ];
        if (this.usedSpriteAudioCache[audio.spriteAudioName].length === audio.spriteNames.length &&
            !playOnce) {
            this.usedSpriteAudioCache[audio.spriteAudioName] = [];
        }
    }
    getUnusedAudioNames(audio) {
        if (!this.usedSpriteAudioCache[audio.spriteAudioName])
            return audio.spriteNames;
        return audio.spriteNames.filter(name => !this.usedSpriteAudioCache[audio.spriteAudioName].includes(name));
    }
    getAudioNameFromSprites(audioName) {
        let endAudioName = '';
        for (const [key, value] of Object.entries(this.spriteCache)) {
            const isInArray = value.filter(name => name.includes(audioName));
            if (isInArray.length > 0)
                endAudioName = key;
        }
        return endAudioName;
    }
    findAudioInSprites(audioName) {
        const endAudioName = this.getAudioNameFromSprites(audioName);
        if (!endAudioName) {
            throw new Error(errorManager.showBox(`zvuk ${audioName} neexistuje, niekde je chyba pri loadingu`));
        }
        return endAudioName;
    }
    getSpriteAudioObject(endAudioName) {
        let endSpriteAudio;
        this.audioRepository.forEach((audioObject) => {
            if (audioObject.audioName.startsWith(endAudioName)) {
                endSpriteAudio = audioObject;
            }
        });
        return endSpriteAudio;
    }
    handleNormalPlayAudio(audioName, playOnce) {
        const audios = this.createSoloAudioArray(audioName);
        if (this.isNotAvailableSound(audioName, audios)) {
            if (playOnce)
                return [undefined, ''];
            this.lastPlayedSoloAudio(audioName);
        }
        const availableIndices = this.getAvailableIndices(audioName, audios);
        const randomIndex = this.audioRandomizer.getRandomIndex(availableIndices);
        this.addRandomIndexToArray(audioName, randomIndex);
        const audio = audios[randomIndex];
        if (!audio) {
            throw new Error(errorManager.showBox(`zvuk ${audioName} neexistuje, niekde je chyba pri loadingu`));
        }
        return [audio, ''];
    }
    addRandomIndexToArray(audioName, index) {
        if (this.playedAudioIndexes[audioName]) {
            this.playedAudioIndexes[audioName].push(index);
        }
        else {
            this.playedAudioIndexes[audioName] = [index];
        }
    }
    createSoloAudioArray(audioName) {
        const audios = [];
        this.audioRepository.forEach((audioObject) => {
            if (audioObject.audioName.startsWith(audioName)) {
                audios.push(audioObject);
            }
        });
        return audios;
    }
    getAvailableIndices(audioName, audios) {
        const availableIndices = [];
        for (let i = 0; i < audios.length; i++) {
            if (!this.playedAudioIndexes[audioName]?.includes(i)) {
                availableIndices.push(i);
            }
        }
        return availableIndices;
    }
    lastPlayedSoloAudio(audioName) {
        let lastPlayed;
        if (this.playedAudioIndexes[audioName].length > 1) {
            lastPlayed = this.playedAudioIndexes[audioName]?.pop();
        }
        this.playedAudioIndexes[audioName] = [];
        if (lastPlayed !== undefined) {
            this.playedAudioIndexes[audioName].push(lastPlayed);
        }
    }
    isNotAvailableSound(audioName, audios) {
        return this.playedAudioIndexes[audioName]?.length >= audios.length;
    }
    pauseAllAudio() {
        for (const [key, audio] of Object.entries(this.playingAudioRepository)) {
            const category = audio?.audioObject.category;
            const exception = category === this.TUTORIAL_EXCEPTION_CATEGORY && modes.isTutorial();
            if (audio?.howl && !exception)
                audio.howl.pause(Number(key));
        }
    }
    unpauseAllAudio() {
        for (const [key, audio] of Object.entries(this.playingAudioRepository)) {
            const category = audio?.audioObject.category;
            const exception = category === this.TUTORIAL_EXCEPTION_CATEGORY && modes.isTutorial();
            if (audio?.howl && !exception)
                audio.howl.play(Number(key));
        }
    }
    stopAllAudio() {
        for (const [key, audio] of Object.entries(this.playingAudioRepository)) {
            if (audio?.howl)
                audio.howl.stop(Number(key));
        }
        this.playingAudioRepository = {};
        this.playingAudioIndex = {};
    }
    stopAudioByName(audioName) {
        if (!this.isAudioPlaying(audioName))
            return;
        for (const [key, audio] of Object.entries(this.playingAudioRepository)) {
            if (audio.audioName.startsWith(audioName)) {
                audio.howl?.stop(Number(key));
                delete this.playingAudioRepository[key];
            }
            else if (audio.spriteAudioName.startsWith(audioName)) {
                audio.howl?.stop(Number(key));
                delete this.playingAudioRepository[key];
            }
        }
        delete this.playingAudioIndex[audioName];
    }
    stopAudioByGroup(audioGroup) {
        for (const audio of Object.values(this.playingAudioRepository)) {
            if (audio.audioObject.group === audioGroup) {
                if (audio.audioObject.sprite) {
                    for (const spriteName of Object.keys(audio.audioObject.sprite)) {
                        this.stopAudioByName(spriteName);
                    }
                }
                else {
                    this.stopAudioByName(audio.audioName);
                }
                delete this.playingAudioIndex[audio.audioName];
            }
        }
    }
    unloadAll() {
        for (const audio of Object.values(this.playingAudioRepository)) {
            audio.howl?.unload();
        }
        this.playingAudioIndex = {};
    }
    updateEnabled = (settingValue) => {
        console.log(`update audio ${settingValue}`);
        Howler.volume(settingValue === 1 ? 4 / 10 : 0);
    };
}
export { AudioManager };
//# sourceMappingURL=AudioManager.js.map